import classNames from "classnames";
import Image from "components/imageNew/image";
import Markdown from "components/markdown/markdown";
import AuthorImageBackground from "content-ui/author/components/authorImageBackground";
import styles from "content-ui/author/components/author.module.css";
import {
  EmailButton,
  LinkedInButton,
  SignalButton,
  TwitterButton,
} from "components/socialButtons/socialButtons";
import { imageLoader } from "utils";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

type AuthorHeroProps = {
  name: string;
  bio: string;
  image?: string;
  socials: {
    twitter?: string;
    linkedin?: string;
    signal?: string;
    email?: string;
  };
};

export default function AuthorHero({
  image,
  name,
  bio,
  socials = {
    twitter: "",
    linkedin: "",
    signal: "",
    email: "",
  },
}: AuthorHeroProps) {
  const { track } = useMixpanelTracking();

  const backgroundGridClasses = classNames(
    styles.backgroundGrid,
    "w-full bg-[length:16px_16px] md:bg-[length:32px_32px] max-w-1200",
    "mb-4 flex items-center justify-center py-[25px]"
  );

  const { twitter, linkedin, signal, email } = socials;

  if (!name) return null;
  return (
    <div className={backgroundGridClasses}>
      <div className="flex flex-col items-center justify-center p-[16px] px-12 md:max-w-[940px] md:flex-row lg:min-w-auto">
        {image ? (
          <AuthorImageBackground className="relative mb-[21px] flex-shrink-0 md:mb-[10px] md:mr-[32px]">
            <Image
              data-testid="authorImage"
              loader={imageLoader}
              src={image}
              alt={`${name} author profile`}
              width={132}
              height={132}
              quality={75}
              className="relative h-[90px] w-[90px] rounded-[10px] object-cover md:h-[132px] md:w-[132px]"
              sizes="(min-width: 736px) 132px, 90px"
            />
          </AuthorImageBackground>
        ) : null}
        <div className="flex flex-col items-center justify-center md:items-start">
          <h2 className="mb-[12px] font-recklessBold text-[24px] leading-[120%] text-black-rock md:mb-3 md:text-[52px]">
            {name}
          </h2>

          {bio && (
            <Markdown
              className="mb-4 text-[14px] font-normal md:mb-[18px] md:block md:text-sm"
              text={bio}
            />
          )}

          <div className="flex items-center gap-x-8 lg:justify-end">
            <TwitterButton
              href={twitter ? `https://twitter.com/${twitter}` : undefined}
              onClick={async () => {
                await track("Author hero click", {
                  Author: name,
                  Type: "Twitter",
                  "Click link": `https://twitter.com/${twitter}`,
                });
              }}
            />
            <LinkedInButton
              href={linkedin}
              onClick={async () => {
                await track("Author hero click", {
                  Author: name,
                  Type: "LinkedIn",
                  "Click link": linkedin,
                });
              }}
            />
            <EmailButton
              href={email ? `mailto:${email}` : undefined}
              onClick={async () => {
                await track("Author hero click", {
                  Author: name,
                  Type: "Email",
                });
              }}
            />
            <SignalButton
              signalNumber={signal}
              onClick={async () => {
                await track("Author hero click", {
                  Author: name,
                  Type: "Signal",
                });
                navigator.clipboard.writeText(`${signal}`);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
